import { useEffect, useState, useTransition } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { PartyDetail } from "@/lib/types";
import { DATE_FILTER, TEXT_FILTER, filterRows, optionsFilter } from "@/lib/columnFilters";
import { sortRows } from "@/lib/sortRows";
import { fmtDateTime, fmtDayDate, fmtTime } from "@/lib/dates";
import { ContextMenu } from "@/components/menu/ContextMenu";
import { SkeletonTableRows } from "@/components/feedback/Skeleton";
import { FillerCell, ListColumnsMenu, ListTable, columnSpan, filterKinds, useLocalListTable, type ListColumn } from "@/components/table/ListTable";
import { CHANGED_COLUMNS, CHANGED_VALUES, changedCell, type ChangedColumnId } from "@/components/table/changedColumns";
import { Modal, ModalForm } from "@/features/warehouse/WarehouseParts";
import { InvitePeopleModal } from "@/features/portal/InvitePeopleModal";
import { PortalCarriersSetting } from "./PortalCarriers";
import {
  PARTNER_LANGUAGES,
  invitePartnerUser,
  listPartnerUsers,
  partnerUserKeys,
  reinvitePartnerUser,
  resetPartnerUser,
  setPortalStock,
  unlinkPartnerUser,
  unlockPartnerUser,
  updatePartnerUser,
  type Invitation,
  type PartnerLanguage,
  type PartnerUser,
  type PortalStock,
} from "./partnerUsers.api";

// The tab Portaal on a relation (docs/partner-portal.md, docs/auth.md §3): the people of this customer
// who sign in on the customer portal. Willems makes the accounts — a customer never registers itself —
// and the invitation goes to their own address: a link that works once, for seven days, with which they
// choose a password and link an authenticator app. The same address on a second relation is the same
// person, who chooses after signing in which relation they work for. Access set up again (a new phone,
// or a forgotten password with the phone gone too) sends a new invitation and ends every session.
// The actions per person sit in a menu (H-b01); more people at once take the list of the transition (§15).

type PortalStatus = "active" | "invited" | "expired" | "locked" | "disabled";

function portalStatus(user: PartnerUser): PortalStatus {
  if (user.status === "disabled") return "disabled";
  if (user.lockedUntil) return "locked";
  if (user.status === "invited") {
    return user.invitationExpiresAt && new Date(user.invitationExpiresAt) > new Date() ? "invited" : "expired";
  }
  return "active";
}

// On the eight status colours (docs/design.md rule 8).
const STATUS_LOOK: Record<PortalStatus, { className: string; labelKey: string }> = {
  active: { className: "wl-status--delivered", labelKey: "portal.status.active" },
  invited: { className: "wl-status--planned", labelKey: "portal.status.invited" },
  expired: { className: "wl-status--delayed", labelKey: "portal.status.expired" },
  locked: { className: "wl-status--onhold", labelKey: "portal.status.locked" },
  disabled: { className: "wl-status--cancelled", labelKey: "portal.status.disabled" },
};

const STATUS_LABEL_KEYS: Record<string, string> = Object.fromEntries(Object.entries(STATUS_LOOK).map(([status, look]) => [status, look.labelKey]));
const LANGUAGE_LABEL_KEYS: Record<string, string> = { nl: "portal.language.nl", en: "portal.language.en", de: "portal.language.de" };

type ColumnId = "name" | "email" | "language" | "status" | "lastLoginAt" | "invitedAt" | "otherParties" | ChangedColumnId;

interface Column extends ListColumn {
  id: ColumnId;
}

const COLUMNS: readonly Column[] = [
  { id: "name", labelKey: "portal.colName", sortKey: "name", locked: true, filter: TEXT_FILTER, defaultWidth: 200 },
  { id: "email", labelKey: "portal.colEmail", sortKey: "email", filter: TEXT_FILTER, defaultWidth: 240 },
  { id: "language", labelKey: "portal.colLanguage", sortKey: "language", filter: optionsFilter(LANGUAGE_LABEL_KEYS), defaultWidth: 120 },
  { id: "status", labelKey: "portal.colStatus", sortKey: "status", filter: optionsFilter(STATUS_LABEL_KEYS), defaultWidth: 180 },
  { id: "lastLoginAt", labelKey: "portal.colLastLogin", sortKey: "lastLoginAt", filter: DATE_FILTER, defaultWidth: 160 },
  { id: "invitedAt", labelKey: "portal.colInvited", sortKey: "invitedAt", filter: DATE_FILTER, defaultWidth: 220 },
  { id: "otherParties", labelKey: "portal.colOtherParties", sortKey: "otherParties", filter: TEXT_FILTER, defaultWidth: 200 },
  ...CHANGED_COLUMNS,
];

const DEFAULT_SORT = { sort: "name", order: "asc" } as const;

type Dialog =
  | { kind: "invite" }
  | { kind: "batch" }
  | { kind: "sent"; user: PartnerUser; invitation: Invitation }
  | { kind: "linked"; user: PartnerUser }
  | { kind: "reset"; user: PartnerUser }
  | { kind: "unlink"; user: PartnerUser };

export function PortalTab({ party, userId }: { party: PartyDetail; userId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: partnerUserKeys.byParty(party.id), queryFn: () => listPartnerUsers(party.id) });
  const [users, setUsers] = useState<PartnerUser[]>([]);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [menu, setMenu] = useState<{ user: PartnerUser; x: number; y: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const { columns, sort, filters } = useLocalListTable("partner-users", COLUMNS, userId, DEFAULT_SORT);
  const span = columnSpan(columns);
  const mail = query.data?.mail;
  const canInvite = mail ? mail.configured || mail.linksShown : false;

  useEffect(() => setUsers(query.data?.users ?? []), [query.data]);

  const others = (user: PartnerUser) => user.parties.filter((p) => p.id !== party.id).map((p) => p.name).join(", ");
  const values = {
    name: (user: PartnerUser) => user.name,
    email: (user: PartnerUser) => user.email,
    language: (user: PartnerUser) => user.language,
    status: (user: PartnerUser) => portalStatus(user),
    lastLoginAt: (user: PartnerUser) => user.lastLoginAt,
    invitedAt: (user: PartnerUser) => user.invitedAt,
    otherParties: others,
    ...CHANGED_VALUES,
  };
  const rows = sortRows(filterRows(users, filters.values, filterKinds(COLUMNS), values), sort, values);

  function replace(next: PartnerUser) {
    setUsers((prev) => [...prev.filter((user) => user.id !== next.id), next]);
    void queryClient.invalidateQueries({ queryKey: partnerUserKeys.byParty(party.id) });
  }

  function run(work: () => Promise<void>) {
    setError(null);
    startTransition(async () => {
      try {
        await work();
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  function act(action: "reinvite" | "reset" | "unlock" | "toggle" | "unlink", user: PartnerUser) {
    if (action === "reset") setDialog({ kind: "reset", user });
    else if (action === "unlink") setDialog({ kind: "unlink", user });
    else if (action === "unlock") run(async () => replace((await unlockPartnerUser(user.id)).user));
    else if (action === "toggle") run(async () => replace((await updatePartnerUser(user.id, { isActive: user.status === "disabled" })).user));
    else
      run(async () => {
        const result = await reinvitePartnerUser(user.id);
        replace(result.user);
        setDialog({ kind: "sent", user: result.user, invitation: result.invitation });
      });
  }

  function cell(id: ColumnId, user: PartnerUser) {
    switch (id) {
      case "name":
        return <td key={id}>{user.name}</td>;
      case "email":
        return <td key={id}>{user.email}</td>;
      case "language":
        return <td key={id}>{t(LANGUAGE_LABEL_KEYS[user.language] as never)}</td>;
      case "status": {
        const status = portalStatus(user);
        const look = STATUS_LOOK[status];
        const label =
          status === "locked" && user.lockedUntil
            ? t("portal.status.lockedUntil", { time: fmtTime(new Date(user.lockedUntil)) })
            : status === "invited" && user.invitationExpiresAt
              ? t("portal.status.invitedUntil", { date: fmtDayDate(new Date(user.invitationExpiresAt)) })
              : t(look.labelKey as never);
        return (
          <td key={id}>
            <span className={`wl-status ${look.className}`}>
              <span className="wl-status__dot" />
              {label}
            </span>
          </td>
        );
      }
      case "lastLoginAt":
        return (
          <td key={id} className={user.lastLoginAt ? "mono" : "t-muted"}>
            {user.lastLoginAt ? fmtDateTime(new Date(user.lastLoginAt)) : t("portal.never")}
          </td>
        );
      case "invitedAt":
        return (
          <td key={id}>
            {user.invitedAt ? (
              <>
                <span className="mono">{fmtDateTime(new Date(user.invitedAt))}</span>
                {user.invitedBy?.name && <span className="t-caption t-muted"> {t("portal.invitedBy", { name: user.invitedBy.name })}</span>}
              </>
            ) : (
              "—"
            )}
          </td>
        );
      case "otherParties":
        return <td key={id}>{others(user) || "—"}</td>;
      case "changedAt":
      case "changedBy":
        return changedCell(id, user, t);
    }
  }

  return (
    <div className="wl-list">
      <div className="wl-hstack" style={{ marginBottom: "var(--space-4)", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ maxWidth: "760px" }}>
          <div className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
            {t("portal.introTitle")}
          </div>
          <p className="t-body-sm t-muted" style={{ margin: 0 }}>
            {t("portal.intro", { party: party.name })}
          </p>
        </div>
        <span className="wl-spacer" />
        <ListColumnsMenu columns={columns} />
        <button type="button" className="wl-btn wl-btn--secondary" disabled={!canInvite} onClick={() => setDialog({ kind: "batch" })}>
          {t("portal.inviteMore")}
        </button>
        <button type="button" className="wl-btn wl-btn--accent" disabled={!canInvite} onClick={() => setDialog({ kind: "invite" })}>
          {t("portal.invite")}
        </button>
      </div>

      {query.data?.stock.customer && (
        <PortalStockSetting
          stock={query.data.stock}
          disabled={isPending}
          onChange={(stock) =>
            run(async () => {
              await setPortalStock(party.id, stock);
              await queryClient.invalidateQueries({ queryKey: partnerUserKeys.byParty(party.id) });
            })
          }
        />
      )}
      {query.data?.stock.customer && <PortalCarriersSetting party={party} />}
      {mail && !mail.configured && (
        <div className={`wl-alert ${mail.linksShown ? "wl-alert--info" : "wl-alert--warning"}`} style={{ marginBottom: "var(--space-3)" }}>
          <span>{t(mail.linksShown ? "portal.mailOffLocal" : "portal.mailOff")}</span>
        </div>
      )}
      {error && (
        <div className="wl-alert wl-alert--danger" style={{ marginBottom: "var(--space-3)" }} role="alert">
          <span>{error}</span>
        </div>
      )}

      <ListTable columns={columns} sort={sort} filters={filters} trailing={{ label: t("portal.colActions"), width: 56 }}>
        <tbody aria-busy={query.isLoading || undefined}>
          {query.isLoading && rows.length === 0 && <SkeletonTableRows columns={span + 1} />}
          {!query.isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={span + 1} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "var(--space-8)" }}>
                {t("portal.empty")}
              </td>
            </tr>
          )}
          {rows.map((user) => (
            <tr key={user.id}>
              {columns.visible.map((column) => cell(column.id, user))}
              <FillerCell columns={columns} />
              <td>
                <div className="wl-hstack" style={{ justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="wl-btn wl-btn--ghost wl-btn--icon wl-btn--sm"
                    aria-label={t("portal.menu", { name: user.name })}
                    aria-haspopup="menu"
                    disabled={isPending}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenu({ user, x: rect.right - 300, y: rect.bottom + 4 });
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <circle cx="5" cy="12" r="1.6" />
                      <circle cx="12" cy="12" r="1.6" />
                      <circle cx="19" cy="12" r="1.6" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </ListTable>
      <p className="t-caption t-muted" style={{ margin: "var(--space-2) 0 0" }}>
        {t("portal.changedNote")}
      </p>

      {menu && <UserMenu user={menu.user} x={menu.x} y={menu.y} party={party} canInvite={canInvite} onClose={() => setMenu(null)} act={act} />}

      {dialog?.kind === "invite" && (
        <InviteModal
          party={party}
          onClose={() => setDialog(null)}
          onDone={(result) => {
            replace(result.user);
            setDialog(result.invitation ? { kind: "sent", user: result.user, invitation: result.invitation } : { kind: "linked", user: result.user });
          }}
        />
      )}
      {dialog?.kind === "batch" && (
        <InvitePeopleModal
          party={party}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            void queryClient.invalidateQueries({ queryKey: partnerUserKeys.byParty(party.id) });
          }}
        />
      )}
      {dialog?.kind === "sent" && <SentModal user={dialog.user} invitation={dialog.invitation} onClose={() => setDialog(null)} />}
      {dialog?.kind === "linked" && (
        <Modal title={t("portal.linkedTitle")} onClose={() => setDialog(null)}>
          <Message text={t("portal.linkedExplain", { name: dialog.user.name })} onClose={() => setDialog(null)} />
        </Modal>
      )}
      {dialog?.kind === "reset" && (
        <ConfirmModal
          title={t("portal.resetTitle", { name: dialog.user.name })}
          text={t("portal.resetExplain", { name: dialog.user.name, email: dialog.user.email })}
          confirmLabel={t("portal.resetConfirm")}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            const result = await resetPartnerUser(dialog.user.id);
            replace(result.user);
            setDialog({ kind: "sent", user: result.user, invitation: result.invitation });
          }}
        />
      )}
      {dialog?.kind === "unlink" && (
        <ConfirmModal
          title={t("portal.unlinkTitle", { name: dialog.user.name, party: party.name })}
          text={`${t("portal.unlinkExplain", { name: dialog.user.name })}${dialog.user.parties.length === 1 ? ` ${t("portal.unlinkLast", { name: dialog.user.name })}` : ""}`}
          confirmLabel={t("portal.unlinkConfirm")}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await unlinkPartnerUser(party.id, dialog.user.id);
            setUsers((prev) => prev.filter((user) => user.id !== dialog.user.id));
            void queryClient.invalidateQueries({ queryKey: partnerUserKeys.byParty(party.id) });
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function UserMenu({
  user,
  x,
  y,
  party,
  canInvite,
  onClose,
  act,
}: {
  user: PartnerUser;
  x: number;
  y: number;
  party: PartyDetail;
  canInvite: boolean;
  onClose: () => void;
  act: (action: "reinvite" | "reset" | "unlock" | "toggle" | "unlink", user: PartnerUser) => void;
}) {
  const { t } = useTranslation();
  const status = portalStatus(user);
  const waiting = status === "invited" || status === "expired";
  return (
    <ContextMenu x={x} y={y} label={t("portal.menu", { name: user.name })} onClose={onClose}>
      {waiting && (
        <button type="button" role="menuitem" className="wl-menu__item" aria-disabled={!canInvite || undefined} onClick={() => canInvite && act("reinvite", user)}>
          {t("portal.reinvite")}
        </button>
      )}
      {user.status === "active" && (
        <button
          type="button"
          role="menuitem"
          className="wl-menu__item"
          style={{ flexDirection: "column", alignItems: "flex-start", gap: "var(--space-0_5)" }}
          aria-disabled={!canInvite || undefined}
          onClick={() => canInvite && act("reset", user)}
        >
          <span>{t("portal.reset")}</span>
          <span className="t-caption t-muted" style={{ fontWeight: "var(--fw-regular)", whiteSpace: "normal" }}>
            {t("portal.resetHint", { name: user.name })}
          </span>
        </button>
      )}
      <button type="button" role="menuitem" className="wl-menu__item" aria-disabled={status !== "locked" || undefined} onClick={() => status === "locked" && act("unlock", user)}>
        {t("portal.unlock")}
        {status !== "locked" && <span className="wl-menu__hint t-caption t-muted">{t("portal.notLocked")}</span>}
      </button>
      <div className="wl-menu__divider" />
      <button type="button" role="menuitem" className="wl-menu__item" onClick={() => act("unlink", user)}>
        {t("portal.unlinkFrom", { party: party.name })}
      </button>
      <button
        type="button"
        role="menuitem"
        className={`wl-menu__item${user.status === "disabled" ? "" : " wl-menu__item--danger"}`}
        onClick={() => act("toggle", user)}
      >
        {t(user.status === "disabled" ? "portal.turnOn" : "portal.turnOff")}
      </button>
    </ContextMenu>
  );
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InviteModal({
  party,
  onClose,
  onDone,
}: {
  party: PartyDetail;
  onClose: () => void;
  onDone: (result: { user: PartnerUser; invitation?: Invitation }) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [language, setLanguage] = useState<PartnerLanguage>("nl");
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const emailWrong = email.trim() !== "" && !EMAIL.test(email.trim());

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        onDone(await invitePartnerUser(party.id, { email: email.trim(), displayName: name.trim(), language }));
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  return (
    <Modal title={t("portal.inviteTitle", { party: party.name })} onClose={onClose}>
      <ModalForm
        onSubmit={submit}
        onCancel={onClose}
        error={error}
        isPending={isPending}
        submitLabel={t("portal.inviteSend")}
        disabled={!EMAIL.test(email.trim()) || name.trim().length < 2}
      >
        <p className="t-body-sm t-muted" style={{ margin: 0 }}>
          {t("portal.inviteExplain", { party: party.name })}
        </p>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="portal-name">
            {t("portal.colName")}
          </label>
          <input id="portal-name" className="wl-input" value={name} maxLength={100} autoFocus onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="portal-language">
            {t("portal.languageLabel")}
          </label>
          <select id="portal-language" className="wl-select" value={language} onChange={(e) => setLanguage(e.target.value as PartnerLanguage)}>
            {PARTNER_LANGUAGES.map((option) => (
              <option key={option} value={option}>
                {t(LANGUAGE_LABEL_KEYS[option] as never)}
              </option>
            ))}
          </select>
        </div>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="portal-email">
            {t("portal.colEmail")}
          </label>
          <input
            id="portal-email"
            className="wl-input mono"
            type="email"
            value={email}
            maxLength={254}
            aria-invalid={emailWrong || undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
          {emailWrong ? (
            <span className="wl-field__help" style={{ color: "var(--text-danger)" }} role="alert">
              {t("portal.emailInvalid")}
            </span>
          ) : (
            <span className="wl-field__help">{t("portal.emailHelp")}</span>
          )}
        </div>
      </ModalForm>
    </Modal>
  );
}

function SentModal({ user, invitation, onClose }: { user: PartnerUser; invitation: Invitation; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const until = fmtDateTime(new Date(invitation.expiresAt));
  // Mailed; locally without mail the link comes back instead (nothing went wrong); or the mail failed.
  const outcome = invitation.mailed ? "sent" : invitation.link ? "local" : "failed";
  const titleKey = { sent: "portal.sentTitle", local: "portal.localTitle", failed: "portal.notSentTitle" }[outcome];
  return (
    <Modal title={t(titleKey as never)} onClose={onClose}>
      <div className="wl-modal__body">
        <div className="wl-vstack" style={{ gap: "var(--space-4)", alignItems: "stretch", padding: "var(--space-2) var(--space-5) var(--space-4)" }}>
          {outcome === "sent" && (
            <p className="t-body-sm" style={{ margin: 0 }}>
              {t("portal.sentExplain", { name: user.name, email: user.email, until })}
            </p>
          )}
          {outcome === "local" && (
            <p className="t-body-sm" style={{ margin: 0 }}>
              {t("portal.localExplain", { name: user.name, until })}
            </p>
          )}
          {outcome === "failed" && (
            <div className="wl-alert wl-alert--warning">
              <span>{t("portal.notSent")}</span>
            </div>
          )}
          {invitation.link && (
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="portal-link">
                {t("portal.localLink")}
              </label>
              <div className="wl-hstack" style={{ gap: "var(--space-2)" }}>
                <input id="portal-link" className="wl-input mono" value={invitation.link} readOnly onFocus={(e) => e.target.select()} />
                <button
                  type="button"
                  className="wl-btn wl-btn--secondary"
                  onClick={() => void navigator.clipboard.writeText(invitation.link!).then(() => setCopied(true))}
                >
                  {t(copied ? "portal.copied" : "portal.copy")}
                </button>
              </div>
            </div>
          )}
          <div className="wl-hstack" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="wl-btn wl-btn--primary" onClick={onClose}>
              {t("portal.done")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Message({ text, onClose }: { text: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="wl-modal__body">
      <div className="wl-vstack" style={{ gap: "var(--space-4)", alignItems: "stretch", padding: "var(--space-2) var(--space-5) var(--space-4)" }}>
        <p className="t-body-sm" style={{ margin: 0 }}>
          {text}
        </p>
        <div className="wl-hstack" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="wl-btn wl-btn--primary" onClick={onClose}>
            {t("portal.done")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConfirmModal({
  title,
  text,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  title: string;
  text: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  return (
    <Modal title={title} onClose={onClose}>
      <ModalForm
        onSubmit={() => {
          setError(null);
          startTransition(async () => {
            try {
              await onConfirm();
            } catch (err) {
              setError(err instanceof Error ? err.message : t("common.errorGeneric"));
            }
          });
        }}
        onCancel={onClose}
        error={error}
        isPending={isPending}
        submitLabel={confirmLabel}
      >
        <p className="t-body-sm" style={{ margin: 0 }}>
          {text}
        </p>
      </ModalForm>
    </Modal>
  );
}

/**
 * Voorraad in het portaal (docs/partner-portal.md §16 step 7): Willems turns it on per customer. The portal asks the
 * warehouse for the stock of the owners below when the customer looks; without an owner it shows nothing, and the
 * portal keeps Voorraad out of its bar.
 */
function PortalStockSetting({ stock, disabled, onChange }: { stock: PortalStock; disabled: boolean; onChange: (stock: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <div className="wl-hstack" style={{ marginBottom: "var(--space-3)", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" }}>
      <label className="wl-hstack t-body-sm" style={{ gap: "var(--space-2)", alignItems: "center", cursor: "pointer" }}>
        <input type="checkbox" checked={stock.enabled} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span style={{ fontWeight: "var(--fw-semibold)" }}>{t("portal.stock")}</span>
      </label>
      <span className="t-body-sm t-muted">
        {stock.owners.length === 0
          ? t("portal.stockNoOwner")
          : t("portal.stockOwners", { count: stock.owners.length, owners: stock.owners.join(", ") })}
      </span>
    </div>
  );
}
